import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Typography, CircularProgress } from "@mui/material";
import axiosInstance from "../axios";
import { toast, ToastContainer } from "react-toastify";
import { IconButton } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

function OrderDetails({ orderId, onDone, businessId, onAssigned }) {
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderQuantity, setOrderQuantity] = useState(null);
  const [quantities, setQuantities] = useState([]);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [fieldErrors, setFieldErrors] = useState([]);
  const {t}= useTranslation();

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId || String(orderId).trim() === "") {
        toast.error("Invalid Order ID.");
        return;
      }

      try {
        const response = await axiosInstance.post(
          "orders.php?method=get_order_details",
          {
            order_id: orderId,
            business_id: businessId,
          }
        );

        const totalresponse = response.data?.orders_count?.[0];

        setOrderDetails(totalresponse);
        const quantity = parseInt(totalresponse?.quantity || 0);
        setOrderQuantity(quantity);
        setQuantities(Array(quantity).fill(""));
        setFieldErrors(Array(quantity).fill(""));

      } catch (error) {
        console.log("Error fetching order details:", error);
        toast.error("Failed to fetch order details.");
      }
    };

    fetchOrderDetails();
  }, [orderId, businessId]);

  const handleQuantityChange = (index, value) => {
    const updated = [...quantities];
    updated[index] = value;
    setQuantities(updated);

    const newErrors = [...fieldErrors];
    newErrors[index] = "";
    setFieldErrors(newErrors);
  };

  const handlePlatesSubmit = async () => {
    setSubmitLoader(true);
    try {
      const tempErrors = Array(quantities.length).fill("");
      let hasError = false;

      const formattedPlates = quantities.map((plate, index) => {
        if (!plate.trim()) {
          tempErrors[index] = "This field is required.";
          hasError = true;
        }
        return plate.trim();
      });

      setFieldErrors(tempErrors);

      if (hasError) {
        setSubmitLoader(false);
        return;
      }

      const payload = {
        order_id: orderId,
        business_id: businessId,
        plates: formattedPlates,
      };

      await axiosInstance.post(
        "orders.php?method=assign_plates",
        payload
      );

      toast.success("Plates assigned successfully ✅");

      setQuantities(Array(quantities.length).fill(""));
      setFieldErrors(Array(quantities.length).fill(""));
      if (onAssigned) {
        onAssigned();
      }

    } catch (error) {
      console.log("Error submitting plates:", error);
      toast.error("Failed to assign plates.");
    } finally {
      setSubmitLoader(false);
    }
  };

  return (
    <Box sx={{ p: "10px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          px: { xs: "10px", sm: "5px" },
        }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto",
            fontSize: "24px",
            fontWeight: "600",
            lineHeight: "16px",
            color: "var(--pg-text)",
            px: "14px",
            pb: "25px",
            pt: "14px",
          }}
        >
        {t("Assign Genie Tags")}
        </Typography>
        <IconButton onClick={() => onDone()} sx={{ color: "primary.main" }}>
          <ChevronLeft sx={{ color: "primary.main" }} />
          <Typography sx={{ fontWeight: "bold", color: "primary.main" }}>
            {t("Back")}
          </Typography>
        </IconButton>
      </Box>

      {orderQuantity !== null && (
        <Typography
          sx={{
            fontSize: "18px",
            textTransform: "capitalize",
            fontFamily: "Roboto",
            fontWeight: "500",
            pl: "6px",
            mt: 4,
          }}
        >
          {orderDetails?.business_name} - {orderDetails?.product_name},
          Quantity: <strong>{orderQuantity}</strong>
        </Typography>
      )}

      <Box sx={{ mt: 1 }}>
        {quantities.map((value, index) => (
          <Box key={index} sx={{ m: 1 }}>
            <TextField
              label={`plate ${index + 1}`}
              value={value}
              onChange={(e) => handleQuantityChange(index, e.target.value)}
              sx={{ borderRadius: "5px", width: { xs: "90%", sm: "50%" } }}
              error={!!fieldErrors[index]}
              helperText={fieldErrors[index]}
            />
          </Box>
        ))}
      </Box>

      {orderQuantity !== null && (
        <Button
          onClick={handlePlatesSubmit}
          variant="contained"
          sx={{
            fontSize: "16px",
            textTransform: "capitalize",
            fontFamily: "Roboto",
            px: "20px",
            ml: "7px",
            mt: "10px",
          }}
        >
          {submitLoader ? (
            <CircularProgress color="white" size={24} />
          ) : (
            t("Submit")
          )}
        </Button>
      )}

      <ToastContainer />
    </Box>
  );
}

export default OrderDetails;
